import {uniqBy} from "lodash"

import PBService from "@irati/utils/services"
import client, {QueryOptions, MutationOptions} from "@irati/utils/services/apollo"
import {Deal, QueryDealFavouritesArgs, MutationDealAddFavouriteArgs, MutationDealRemoveFavouriteArgs} from "@irati/types"
import authentication from "@irati/auth"

import dealsErrorHandler from "./errors/dealsErrorHandler"

import dealFavouritesQuery, {dealFavouritesQueryKey} from "./queries/dealFavourites"
import dealAddFavouriteMutation, {dealAddFavouriteMutationKey} from "./mutations/dealAddFavourite"
import dealRemoveFavouriteMutation, {dealRemoveFavouriteMutationKey} from "./mutations/dealRemoveFavourite"

class FavouriteService extends PBService {
  list(variables: QueryDealFavouritesArgs, options: QueryOptions = {}): Promise<Deal[]> {
    return client.query({...options, query: dealFavouritesQuery, variables}).then(({data}) => data[dealFavouritesQueryKey])
  }

  add(deal: Deal, options: MutationOptions = {}) {
    const user = authentication.user()
    const variables: MutationDealAddFavouriteArgs = {userId: user.uuid, dealId: deal.uuid}

    this.addToFavouritesCache({userId: user.uuid}, deal)
    return client.mutate({...options, mutation: dealAddFavouriteMutation, variables}).then(({data}) => {
      return data[dealAddFavouriteMutationKey]
    })
  }

  remove(deal: Deal, options: MutationOptions = {}) {
    const user = authentication.user()
    const variables: MutationDealRemoveFavouriteArgs = {userId: user.uuid, dealId: deal.uuid}

    this.removeFromFavouritesCache({userId: user.uuid}, deal)
    return client.mutate({...options, mutation: dealRemoveFavouriteMutation, variables}).then(({data}) => {
      return data[dealRemoveFavouriteMutationKey]
    })
  }

  listSync(variables: QueryDealFavouritesArgs): Deal[] {
    try {
      const data = client.readQuery({query: dealFavouritesQuery, variables})
      const deals = data[dealFavouritesQueryKey]
      return deals
    } catch (e) {
      // don't appear to have cache, lets fire and forget a list call, might have cache next time
      this.list(variables)
      return []
    }
  }

  private addToFavouritesCache(variables: QueryDealFavouritesArgs, deal: Deal) {
    const cache = this.listSync(variables)
    const newCache = uniqBy([deal, ...cache], "uuid")
    client.writeQuery({query: dealFavouritesQuery, variables, data: {[dealFavouritesQueryKey]: newCache}})
  }

  private removeFromFavouritesCache(variables: QueryDealFavouritesArgs, deal: Deal) {
    const cache = this.listSync(variables)
    const newCache = uniqBy(cache.filter((t) => t.uuid !== deal.uuid), "uuid")
    client.writeQuery({query: dealFavouritesQuery, variables, data: {[dealFavouritesQueryKey]: newCache}})
  }
}

export default new FavouriteService({errorHandlers: [dealsErrorHandler]})
